import { useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { useNavigate } from 'react-router-dom';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { Button } from '@progress/kendo-react-buttons';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { CardViewDataStateChangeEvent } from './allusers-cardview';
import { LinkCell } from '../components/grid';
import { AllUsersCardView } from './allusers-cardview';
import { GetReportsAdminFacilitatorsAsync } from '../services/allusers';
import { appStore } from '../stores/app-store';
import { useGetAdminFacilitatorUsers } from '../hooks/services/use-users';
import { User } from '../types/user';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'username', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

type GridMode = 'grid' | 'card';

const AdminFacilitatorsGrid = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<GridMode>('grid');

  const { data, isLoading } = useGetAdminFacilitatorUsers(
    toDataSourceRequestString(dataState)
  );

  const LinkedNameCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props}></LinkCell>
  );

  const permissionColumn = (props: GridCellProps) => {
    const roles: string[] = Array.isArray(props.dataItem.roles)
      ? props.dataItem.roles
      : [];

    return (
      <td className={'k-table-td'}>
        <ul className={'mb-0 ps-3'}>
          {roles.map((r: string, idx: number) => (
            <li key={idx}>{r}</li>
          ))}
        </ul>
      </td>
    );
  };

  const StatusColoring = (props: GridCellProps) => {
    const status = props.dataItem.status;
    return (
      <td
        className={`k-table-td ${status === 'Enabled' ? 'text-success' : 'text-muted'}`}>
        <span>{status}</span>
      </td>
    );
  };

  const onRegistrationColoring = (props: GridCellProps) => {
    const confirmed = props.dataItem.emailConfirmed;
    return (
      <td className={`k-table-td ${confirmed ? 'text-success' : 'text-muted'}`}>
        <span>{confirmed ? 'Registered' : 'Not Registered'}</span>
      </td>
    );
  };

  const onAddFacilitator = () => {
    navigate('/administration/facilitators/invite-user');
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const onExportExcel = async () => {
    appStore.showLoading();
    try {
      const response = await GetReportsAdminFacilitatorsAsync();
      if (gridExportRef.current !== null) {
        const mappedResponse = (response?.data ?? []).map((item) => ({
          ...item,
          roles: Array.isArray(item.roles) ? item.roles.join(', ') : item.roles,
          registration: item.emailConfirmed ? 'Registered' : 'Not registered'
        }));
        gridExportRef.current.save(mappedResponse);
      }
    } catch (error) {
      console.error(error);
    } finally {
      appStore.hideLoading();
    }
  };

  return (
    <>
      <Toolbar title={'Facilitator users'}>
        <Button onClick={onAddFacilitator} themeColor={'primary'}>
          Add new
        </Button>
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search facilitator users'}
          columnsToSearch={['username', 'fullName', 'organisation']}
          showCardMode={true}
          exportRef={gridExportRef}
          useCustomExportExcel={true}
          handleCustomExportToExcel={onExportExcel}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode as GridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
        />
        <ExcelExport ref={gridExportRef} fileName={'facilitator-users.xlsx'}>
          <ExcelExportColumn field="username" title="Username" width={250} />
          <ExcelExportColumn field="fullName" title="Name" width={250} />
          <ExcelExportColumn
            field="organisation"
            title="Organisation"
            width={270}
          />
          <ExcelExportColumn
            field="roles"
            title="Assigned Permissions"
            width={200}
          />
          <ExcelExportColumn field="status" title="Status" width={200} />
          <ExcelExportColumn
            field="registration"
            title="Registration"
            width={200}
          />
        </ExcelExport>
        {gridMode === 'grid' && (
          <Grid
            id={'grid-facilitator-user'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'username'}
              filter={'text'}
              title={'Username'}
              columnMenu={ColumnMenu}
              cells={{ data: LinkedNameCell }}
            />
            <Column field={'fullName'} title={'Name'} columnMenu={ColumnMenu} />
            <Column
              field={'organisation'}
              title={'Organisation'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'roles'}
              title={'Assigned Permissions'}
              columnMenu={ColumnMenu}
              cells={{ data: permissionColumn }}
            />
            <Column
              field={'status'}
              title={'Status'}
              columnMenu={ColumnMenu}
              cells={{ data: StatusColoring }}
              width={120}
            />
            <Column
              field={'emailConfirmed'}
              title={'Registration'}
              columnMenu={ColumnMenu}
              cells={{ data: onRegistrationColoring }}
              width={150}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}
        {gridMode === 'card' && (
          <AllUsersCardView
            data={(data?.data ?? []) as User[]}
            onUsernameClick={(id: number) =>
              navigate(`/administration/facilitators/${id}`)
            }
            total={data?.total}
            {...dataState}
            onDataStateChange={(e: CardViewDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
          />
        )}
      </div>
    </>
  );
};

export default AdminFacilitatorsGrid;
